// ═══════════════════════════════════════════════════════════════
// PICHAI — DashboardPage.jsx (version finale, enrichie)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon, BoltIcon, EyeIcon, ArrowTrendingUpIcon,
  ClockIcon, SparklesIcon, PencilSquareIcon, BellIcon,
  ChevronRightIcon, ExclamationTriangleIcon,
  UserCircleIcon, ShieldCheckIcon, LightBulbIcon,
  BuildingOffice2Icon, ChatBubbleLeftRightIcon,
  ArrowUpIcon, FireIcon, CheckCircleIcon,
  DocumentTextIcon, QuestionMarkCircleIcon,
  CpuChipIcon, HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import { dashboardAPI, eventsAPI, fetchHappening } from '../services/api';
import { useAuth } from '../hooks/useUser';

// ═══════════════════════ DESIGN TOKENS ═══════════════════════
const C = {
  bg: '#FFFFFF', surface: '#F7F7F8', surface2: '#F0F0F2',
  border: '#E8E8EC', borderDark: '#D0D0D8',
  text: '#0A0A0B', textSub: '#52525B', textMuted: '#A1A1AA',
  black: '#0A0A0B', blackHover: '#27272A',
  green: '#059669', greenBg: '#F0FDF4', greenBorder: '#BBF7D0',
  red: '#DC2626', redBg: '#FEF2F2', redBorder: '#FECACA',
  amber: '#D97706', amberBg: '#FFFBEB', amberBorder: '#FDE68A',
  blue: '#2563EB', blueBg: '#EFF6FF', blueBorder: '#BFDBFE',
  activeBg: '#E2EDFD', activeText: '#0A0A0B',
};

// ═══════════════════════════ CSS ═════════════════════════════
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .dash {
    font-family: 'Inter', system-ui, sans-serif;
    background: ${C.bg}; color: ${C.text};
    min-height: 100vh; -webkit-font-smoothing: antialiased;
  }

  .dash-layout {
    display: grid; grid-template-columns: 240px 1fr; min-height: 100vh;
  }
  @media (max-width: 1024px) {
    .dash-layout { grid-template-columns: 1fr; }
    .sidebar { display: none !important; }
  }

  .sidebar {
    background: ${C.bg}; border-right: 1px solid ${C.border};
    display: flex; flex-direction: column;
    position: sticky; top: 60px; height: calc(100vh - 60px);
    overflow-y: auto;
  }

  .sidebar-section { padding: 12px; flex: 1; }

  .sidebar-btn {
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 9px 10px; border-radius: 8px;
    font-size: 14px; font-weight: 500; color: ${C.textSub};
    background: none; border: none; cursor: pointer;
    font-family: 'Inter', sans-serif; text-align: left;
    transition: background 0.12s, color 0.12s;
    white-space: nowrap; text-decoration: none;
  }
  .sidebar-btn:hover { background: ${C.surface2}; color: ${C.text}; }
  .sidebar-btn.active {
    background: ${C.activeBg}; color: ${C.activeText}; font-weight: 600;
  }
  .sidebar-btn.active svg { color: ${C.activeText}; }
  .sidebar-btn svg { flex-shrink: 0; width: 18px; height: 18px; color: ${C.textSub}; }

  .mobile-tabs {
    display: none;
    position: sticky; top: 60px; z-index: 45;
    background: ${C.bg}; border-bottom: 1px solid ${C.border};
    overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none;
  }
  .mobile-tabs::-webkit-scrollbar { display: none; }
  .mobile-tabs-inner { display: flex; gap: 2px; padding: 8px 12px; }
  .mobile-tab {
    flex-shrink: 0; padding: 7px 14px; border-radius: 8px;
    font-size: 13px; font-weight: 500; border: 1px solid transparent;
    background: none; color: ${C.textMuted}; cursor: pointer;
    font-family: 'Inter', sans-serif; transition: all 0.12s; white-space: nowrap;
  }
  .mobile-tab:hover { background: ${C.surface2}; color: ${C.text}; }
  .mobile-tab.active {
    background: ${C.activeBg}; color: ${C.activeText}; font-weight: 600;
  }
  @media (max-width: 1024px) { .mobile-tabs { display: block; } }

  .main-content {
    padding: 28px 28px 64px; max-width: 1200px; margin: 0 auto; width: 100%;
  }
  @media (max-width: 768px) { .main-content { padding: 16px 16px 48px; } }

  .card {
    background: ${C.bg}; border: 1px solid ${C.border}; border-radius: 12px; overflow: hidden;
  }
  .card-header {
    display: flex; align-items: center; gap: 8px;
    padding: 14px 18px; border-bottom: 1px solid ${C.border};
  }
  .card-title { font-size: 13px; font-weight: 700; color: ${C.text}; letter-spacing: -0.01em; }
  .card-badge { margin-left: auto; font-size: 11px; color: ${C.textMuted}; font-weight: 500; }

  .metrics-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px;
  }
  @media (max-width: 1100px) { .metrics-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 580px)  { .metrics-grid { grid-template-columns: 1fr 1fr; } }

  .metric-tile {
    padding: 18px 20px; border-radius: 12px; border: 1px solid ${C.border}; background: ${C.bg};
    transition: box-shadow 0.15s, border-color 0.15s;
  }
  .metric-tile:hover { border-color: ${C.borderDark}; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
  .metric-icon {
    width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center;
    justify-content: center; margin-bottom: 12px; background: ${C.surface};
  }
  .metric-value { font-size: 28px; font-weight: 700; color: ${C.text}; line-height: 1; margin-bottom: 4px; font-feature-settings: "tnum"; }
  .metric-label { font-size: 12px; color: ${C.textMuted}; font-weight: 500; margin-bottom: 6px; }
  .metric-delta { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 20px; }
  .delta-up   { background: ${C.greenBg}; color: ${C.green}; }
  .delta-flat { background: ${C.surface}; color: ${C.textMuted}; }
  .delta-down { background: ${C.redBg};   color: ${C.red}; }

  .two-col { display: grid; grid-template-columns: 1fr 300px; gap: 16px; }
  @media (max-width: 1100px) { .two-col { grid-template-columns: 1fr; } }

  .feed-row {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-bottom: 1px solid ${C.border};
    cursor: pointer; transition: background 0.1s;
  }
  .feed-row:last-child { border-bottom: none; }
  .feed-row:hover { background: ${C.surface}; }
  .feed-icon {
    width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; background: ${C.surface};
  }
  .feed-title {
    font-size: 13px; font-weight: 600; color: ${C.text}; margin-bottom: 4px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .feed-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

  .claim-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; border-bottom: 1px solid ${C.border};
    cursor: pointer; transition: background 0.1s;
  }
  .claim-row:last-child { border-bottom: none; }
  .claim-row:hover { background: ${C.surface}; }
  .claim-row-title {
    font-size: 13px; font-weight: 600; color: ${C.text}; flex: 1; min-width: 0;
    padding-right: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .claim-row-foot { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }

  .tag {
    display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 4px;
    font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; border: 1px solid;
  }
  .tag-green  { background: ${C.greenBg};  color: ${C.green};  border-color: ${C.greenBorder}; }
  .tag-red    { background: ${C.redBg};    color: ${C.red};    border-color: ${C.redBorder}; }
  .tag-amber  { background: ${C.amberBg};  color: ${C.amber};  border-color: ${C.amberBorder}; }
  .tag-blue   { background: ${C.blueBg};   color: ${C.blue};   border-color: ${C.blueBorder}; }
  .tag-neutral{ background: ${C.surface};  color: ${C.textSub};border-color: ${C.border}; }

  .prog-track { height: 5px; border-radius: 3px; background: ${C.surface2}; overflow: hidden; }
  .prog-fill { height: 100%; border-radius: 3px; transition: width 1.2s cubic-bezier(.22,1,.36,1); }

  .btn-black {
    background: ${C.black}; color: #fff; border: none; border-radius: 8px;
    padding: 9px 18px; font-size: 13px; font-weight: 600; cursor: pointer;
    font-family: 'Inter', sans-serif; transition: background 0.15s;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .btn-black:hover { background: ${C.blackHover}; }

  .btn-outline {
    background: transparent; border: 1px solid ${C.border}; border-radius: 8px;
    padding: 8px 16px; font-size: 13px; color: ${C.textSub}; cursor: pointer;
    font-family: 'Inter', sans-serif; transition: all 0.15s;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .btn-outline:hover { border-color: ${C.text}; color: ${C.text}; }

  .ring-wrap { position: relative; display: inline-flex; align-items: center; justify-content: center; }

  .tabs { display: flex; gap: 2px; margin-bottom: 16px; flex-wrap: wrap; }
  .tab {
    padding: 7px 14px; border-radius: 7px; font-size: 13px; font-weight: 500;
    border: 1px solid transparent; background: none; color: ${C.textMuted}; cursor: pointer;
    font-family: 'Inter', sans-serif; transition: all 0.12s; white-space: nowrap;
  }
  .tab:hover { background: ${C.surface}; color: ${C.text}; }
  .tab.active { background: ${C.activeBg}; color: ${C.activeText}; font-weight: 600; border-color: ${C.activeBg}; }

  .shimmer {
    animation: shimmer 1.8s infinite linear;
    background: linear-gradient(90deg, ${C.surface} 25%, ${C.surface2} 50%, ${C.surface} 75%);
    background-size: 200% 100%; border-radius: 8px;
  }
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  .fade-up { animation: fadeUp 0.3s ease both; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  .space-y > * + * { margin-top: 16px; }

  .bar-chart { display: flex; align-items: flex-end; gap: 6px; height: 80px; }
  .bar-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
  .bar-body { width: 100%; border-radius: 4px 4px 0 0; background: ${C.black}; min-height: 4px; transition: height 0.8s ease; }
  .bar-body.empty { background: ${C.surface2}; }
  .bar-label { font-size: 10px; color: ${C.textMuted}; text-align: center; white-space: nowrap; }

  .impact-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  @media (max-width: 768px) { .impact-stats-grid { grid-template-columns: repeat(2, 1fr); } }

  .inp {
    width: 100%; background: ${C.surface};
    border: 1px solid ${C.border}; border-radius: 8px;
    padding: 10px 14px; font-size: 14px; color: ${C.text};
    font-family: 'Inter', sans-serif; outline: none;
    transition: border-color 0.15s;
  }
  .inp:focus { border-color: ${C.text}; background: ${C.bg}; }
  .inp::placeholder { color: ${C.textMuted}; }
  .inp-label {
    display: block; font-size: 12px; font-weight: 600;
    color: ${C.textSub}; margin-bottom: 6px;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

// ═══════════════ HELPERS ═════════════════════════════════════
const STATUS_CFG = {
  in_progress: { labelKey: 'status_in_progress', tag: 'tag-blue' },
  pending:     { labelKey: 'status_pending',     tag: 'tag-amber' },
  verified:    { labelKey: 'status_verified',    tag: 'tag-green' },
  rejected:    { labelKey: 'status_rejected',    tag: 'tag-red' },
  unverified:  { labelKey: 'status_unverified',  tag: 'tag-neutral' },
};

const TYPE_CFG = {
  vote:     { Icon: ArrowUpIcon,                labelKey: 'type_vote',           tag: 'tag-blue' },
  comment:  { Icon: ChatBubbleLeftRightIcon,    labelKey: 'type_comment',        tag: 'tag-neutral' },
  proof:    { Icon: DocumentTextIcon,           labelKey: 'type_proof',          tag: 'tag-green' },
  question: { Icon: QuestionMarkCircleIcon,     labelKey: 'type_question_ia',    tag: 'tag-amber' },
};

const normalizeClaim = (c) => {
  if (!c) return c;
  return {
    id: c.id, title: c.title || '',
    category: typeof c.category === 'string' ? c.category : c.category?.name || 'Général',
    status: c.status || 'pending',
    claimant: c.claimant || '',
    currentConsensus: (c.crowd_confidence_score != null && (c.total_votes || 0) > 0)
      ? Math.round(c.crowd_confidence_score * 100) : null,
    participants: c.total_votes || 0,
    attention_flag: c.attention_flag || false,
  };
};

const ensureArray = (v) => (Array.isArray(v) ? v : v?.data || []);

const relativeTime = (dateStr) => {
  if (!dateStr) return '—';
  const m = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (m < 1) return 'À l\'instant';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  return `${Math.floor(h / 24)} j`;
};

// ═══════════════ COMPOSANTS RÉUTILISABLES ═══════════════════
const ScoreRing = ({ pct = 0, size = 80, color = C.black }) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (Math.min(Math.max(pct, 0), 100) / 100) * circ;
  return (
    <div className="ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.surface2} strokeWidth={5} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={`${fill} ${circ - fill}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1.2s ease' }} />
      </svg>
      <div style={{ position: 'absolute', textAlign: 'center', fontSize: size * 0.22, fontWeight: 700, color, fontFeatureSettings: '"tnum"' }}>
        {Math.round(pct)}
      </div>
    </div>
  );
};

const Sk = ({ w = '100%', h = 16, r = 8 }) => (
  <div className="shimmer" style={{ width: w, height: h, borderRadius: r }} />
);

// ═══════════════ GRAPHE ACTIVITÉ ════════════════════════════
const ActivityBarChart = ({ activity = [], t }) => {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return { label: d.toLocaleDateString('fr-FR', { month: 'short' }), count: 0, year: d.getFullYear(), month: d.getMonth() };
  });
  activity.forEach(a => {
    if (!a.created_at) return;
    const d = new Date(a.created_at);
    const m = months.find(m => m.year === d.getFullYear() && m.month === d.getMonth());
    if (m) m.count++;
  });
  const max = Math.max(...months.map(m => m.count), 1);

  const stats = [
    { Icon: ArrowUpIcon, key: 'votes', type: 'vote' },
    { Icon: DocumentTextIcon, key: 'proofs', type: 'proof' },
    { Icon: ChatBubbleLeftRightIcon, key: 'comments', type: 'comment' },
    { Icon: QuestionMarkCircleIcon, key: 'questions_ia', type: 'question' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: C.textMuted }}>{t('activity_chart_desc')}</p>
        <span style={{ fontSize: 11, fontWeight: 600 }}>{t('activity_total', { count: activity.length })}</span>
      </div>
      <div className="bar-chart">
        {months.map((m, i) => {
          const hPct = Math.max((m.count / max) * 100, m.count > 0 ? 5 : 0);
          return (
            <div key={i} className="bar-col">
              <div style={{ fontSize: 10, fontWeight: 600, minHeight: 14 }}>{m.count || '—'}</div>
              <div style={{ flex: 1, width: '100%' }}>
                <div className={`bar-body ${m.count === 0 ? 'empty' : ''}`} style={{ height: `${hPct}%` }} />
              </div>
              <div className="bar-label">{m.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, padding: '10px 14px', background: C.surface2, borderRadius: 8 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {stats.map(({ Icon, key, type }, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.textSub }}>
              <Icon style={{ width: 16, height: 16, color: C.textSub }} />
              <span style={{ fontWeight: 600, color: C.text }}>{activity.filter(a => a.type === type).length}</span>
              <span>{t(key)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════ NAVIGATION ═════════════════════════════════
const NAV_ITEMS = [
  { key: 'overview',          labelKey: 'overview',           Icon: ChartBarIcon },
  { key: 'alerts',            labelKey: 'alerts',             Icon: BellIcon },
  { key: 'ecosystem',         labelKey: 'ecosystem',          Icon: BuildingOffice2Icon },
  { key: 'my_reviews',        labelKey: 'my_reviews',         Icon: ChatBubbleLeftRightIcon },
  { key: 'market_trends',     labelKey: 'market_trends',      Icon: ArrowTrendingUpIcon },
  { key: 'activity',          labelKey: 'activity',           Icon: ClockIcon },
  { key: 'impact',            labelKey: 'impact',             Icon: ArrowTrendingUpIcon },
  { key: 'insights',          labelKey: 'insights',           Icon: SparklesIcon },
  { key: 'settings',          labelKey: 'settings',           Icon: PencilSquareIcon },
];

const MobileTabs = ({ activeTab, onTab, t, alertsCount = 0 }) => (
  <div className="mobile-tabs">
    <div className="mobile-tabs-inner">
      {NAV_ITEMS.map(item => (
        <button key={item.key} className={`mobile-tab ${activeTab === item.key ? 'active' : ''}`} onClick={() => onTab(item.key)}>
          {t(item.labelKey)}
          {item.key === 'alerts' && alertsCount > 0 && (
            <span style={{ marginLeft: 4, background: C.red, color: '#fff', borderRadius: 8, fontSize: 9, fontWeight: 700, padding: '1px 5px' }}>
              {alertsCount}
            </span>
          )}
        </button>
      ))}
    </div>
  </div>
);

const Sidebar = ({ activeTab, onTab, navigate, t, alertsCount = 0 }) => (
  <aside className="sidebar">
    <nav className="sidebar-section">
      {NAV_ITEMS.map(({ key, labelKey, Icon }) => (
        <button key={key} className={`sidebar-btn ${activeTab === key ? 'active' : ''}`} onClick={() => onTab(key)}>
          <Icon />
          {t(labelKey)}
          {key === 'alerts' && alertsCount > 0 && (
            <span style={{ marginLeft: 'auto', minWidth: 18, height: 18, background: C.red,
              color: '#fff', borderRadius: 9, fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>
              {alertsCount > 9 ? '9+' : alertsCount}
            </span>
          )}
        </button>
      ))}
    </nav>
    <div style={{ padding: '0 12px 12px' }}>
      <button className="btn-black" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/soumettre-avis')}>
        <PencilSquareIcon style={{ width: 16, height: 16, marginRight: 6 }} /> {t('submit_review')}
      </button>
    </div>
  </aside>
);

// ═══════════════ ONGLET VUE D'ENSEMBLE (complet) ═══════════════
const OverviewTab = ({ personal, insights, recommendations, activity, followedEvents, happening, navigate, t }) => {
  const trustScore = personal?.user?.trust_score ? Math.round(personal.user.trust_score * 100) : 0;
  const votes = personal?.votes || {};
  const evidence = personal?.evidence || {};
  const contributions = personal?.contributions || {};

  const followedCompaniesCount = [...new Set(followedEvents.map(e => e.claimant).filter(Boolean))].length;
  const totalFollowedFacts = followedEvents.length;
  const impactLevel = contributions?.accuracy_rate >= 80 ? 'Élevé' : contributions?.accuracy_rate >= 50 ? 'Moyen' : 'Faible';

  const companies = [...new Set(followedEvents.map(e => e.claimant).filter(Boolean))].slice(0, 4);
  const companiesWithMeta = companies.map(name => ({
    name,
    sector: 'Divers',
    reviews: followedEvents.filter(e => e.claimant === name).length,
    score: Math.round(followedEvents.filter(e => e.claimant === name).reduce((sum, e) => sum + (e.currentConsensus || 0), 0) / (followedEvents.filter(e => e.claimant === name).length || 1))
  }));

  // Happening réel avec fallback
  const liveEvents = (happening && happening.length > 0)
    ? happening.slice(0, 3).map(ev => ({
        id: ev.id,
        title: ev.title,
        currentConsensus: ev.display_score?.percentage ?? 0,
        participants: ev.total_votes || 0,
      }))
    : (recommendations?.slice(0, 3) || [
        { id:1, title:'Digicel facture des frais cachés', currentConsensus: 72, participants: 312 },
        { id:2, title:'Sogener coupe dans zones payantes', currentConsensus: 48, participants: 198 },
        { id:3, title:'Sogebank : frais de clôture non annoncés', currentConsensus: 61, participants: 274 },
      ]);

  return (
    <div className="fade-up">

      {/* 1. Bannière personnalisée */}
      <div className="card" style={{ marginBottom: 16, borderLeft: `4px solid ${C.black}` }}>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                Bonjour, {personal?.user?.first_name || personal?.user?.username || 'Citoyen'} 👋
              </p>
              <p style={{ fontSize: 13, color: C.textSub }}>
                {new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long' })}
              </p>
            </div>
            <span style={{ fontSize: 11, color: C.textMuted, background: C.surface, padding: '4px 10px', borderRadius: 20, border: `1px solid ${C.border}` }}>
              Dernière visite : {personal?.user?.last_login_at ? relativeTime(personal.user.last_login_at) : 'Première connexion'}
            </span>
          </div>
          <p style={{ fontSize: 14, color: C.textSub, lineHeight: 1.7, marginTop: 12 }}>
            {activity?.length > 0
              ? `Depuis votre dernière visite, ${activity.length} nouvelles actions ont été enregistrées sur vos claims suivis.`
              : 'Bienvenue sur PichAI — commencez par explorer les faits et entreprises qui vous concernent.'}
            {insights?.length > 0 && ` L'IA surveille ${insights.length} signaux actifs pour vous.`}
          </p>
        </div>
      </div>

      {/* 5. Boutons d'action rapide */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button className="btn-black" onClick={() => navigate('/chat')}
          style={{ flex: 1, justifyContent: 'center', padding: '12px' }}>
          <CpuChipIcon style={{ width: 16, height: 16, marginRight: 8 }} />
          Poser une question à l'IA
        </button>
        <button className="btn-outline" onClick={() => navigate('/claims')}
          style={{ flex: 1, justifyContent: 'center', padding: '12px' }}>
          <DocumentTextIcon style={{ width: 16, height: 16, marginRight: 8 }} />
          Explorer les faits
        </button>
      </div>

      {/* 6. Happening Events – En ce moment (données réelles) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <BoltIcon style={{ width: 16, color: C.red }} />
          <span className="card-title" style={{ color: C.red }}>En ce moment</span>
          <span className="card-badge" style={{ color: C.red }}>Live</span>
          <button className="btn-outline" style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 11 }} onClick={() => navigate('/claims')}>
            Voir tout →
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
          {liveEvents.map((ev, i) => (
            <div key={i} onClick={() => navigate(`/event/${ev.id}`)}
              style={{ padding: '14px 16px', borderRight: i < 2 ? `1px solid ${C.border}` : 'none',
                cursor: 'pointer', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = C.surface}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.red, animation: 'pulse 2s infinite' }} />
                <span style={{ fontSize: 10, color: C.red, fontWeight: 600 }}>LIVE</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8, lineHeight: 1.4 }}>
                {ev.title}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: C.textMuted }}>{ev.participants} participants</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: ev.currentConsensus >= 50 ? C.green : C.red }}>
                  {ev.currentConsensus}%
                </span>
              </div>
              <div className="prog-track" style={{ marginTop: 6 }}>
                <div className="prog-fill" style={{
                  width: `${ev.currentConsensus}%`,
                  background: ev.currentConsensus >= 50 ? C.green : C.red
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Métriques clés */}
      <div className="metrics-grid">
        {[
          { label: t('trust_score'),         value: `${trustScore}%`, delta: '+3% ce mois', deltaType: 'up', Icon: ShieldCheckIcon },
          { label: t('votes_submitted'),     value: votes?.total || 0, delta: `+${votes?.this_month || 0}`, deltaType: 'up', Icon: ArrowUpIcon },
          { label: t('validated_proofs'),    value: evidence?.approved || 0, delta: `${evidence?.total || 0} soumis`, deltaType: 'flat', Icon: CheckCircleIcon },
          { label: t('vote_accuracy'),       value: `${contributions?.accuracy_rate || 0}%`, delta: t('accuracy_label'), deltaType: contributions?.accuracy_rate >= 70 ? 'up' : 'flat', Icon: LightBulbIcon },
        ].map((tile, i) => (
          <div key={i} className="metric-tile">
            <div className="metric-icon"><tile.Icon style={{ width: 20, height: 20, color: C.black }} /></div>
            <div className="metric-value">{tile.value}</div>
            <div className="metric-label">{tile.label}</div>
            <span className={`metric-delta ${tile.deltaType === 'up' ? 'delta-up' : 'delta-flat'}`}>
              {tile.deltaType === 'up' && <ArrowUpIcon style={{ width: 12, height: 12 }} />}{tile.delta}
            </span>
          </div>
        ))}
      </div>

      {/* Centre de surveillance */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <EyeIcon style={{ width: 18, height: 18, color: C.black }} />
          <span className="card-title">Votre centre de surveillance</span>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: C.textMuted }}>Entreprises</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{followedCompaniesCount}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: C.textMuted }}>Faits suivis</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{totalFollowedFacts}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: C.textMuted }}>Impact</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{impactLevel}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Types d'entités analysées */}
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
        Vous analysez actuellement des :
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <span className="tag tag-blue"><BuildingOffice2Icon style={{ width: 12, height: 12, marginRight: 4 }} />Entreprises</span>
        <span className="tag tag-neutral"><BuildingOffice2Icon style={{ width: 12, height: 12, marginRight: 4 }} />Institutions</span>
        <span className="tag tag-neutral"><UserCircleIcon style={{ width: 12, height: 12, marginRight: 4 }} />Société</span>
      </div>

      {/* Grille 2 colonnes */}
      <div className="two-col">
        <div className="space-y">
          {/* Résumé IA du jour */}
          <div className="card">
            <div className="card-header">
              <SparklesIcon style={{ width: 16, color: C.black }} />
              <span className="card-title">{t('ia_summary_today')}</span>
              <span className="card-badge">{new Date().toLocaleDateString('fr-FR', { day:'numeric', month:'long' })}</span>
            </div>
            <div style={{ padding: '16px 18px' }}>
              <p style={{ fontSize:14, color:C.textSub, lineHeight:1.7 }}>
                {activity?.length ? t('since_last_visit', { count: activity.length }) : t('welcome_message')}
                {insights?.length > 0 && ' ' + t('ia_monitoring', { count: insights.length })}
              </p>
            </div>
          </div>

          {/* Graphique 6 mois */}
          <div className="card">
            <div className="card-header">
              <ClockIcon style={{ width: 16, color: C.black }} />
              <span className="card-title">{t('last_6_months')}</span>
            </div>
            <div style={{ padding: '16px 18px' }}><ActivityBarChart activity={activity || []} t={t} /></div>
          </div>

          {/* 4. Votre impact sur les analyses IA */}
          <div className="card" style={{ background: C.blueBg, border: `1px solid ${C.blueBorder}` }}>
            <div className="card-header" style={{ borderColor: C.blueBorder }}>
              <CpuChipIcon style={{ width: 16, color: C.blue }} />
              <span className="card-title" style={{ color: C.blue }}>Votre impact sur les analyses IA</span>
            </div>
            <div style={{ padding: '16px 18px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                {[
                  { label: 'Participation intégrés', value: votes?.total || 0 },
                  { label: 'Analyses influencées', value: Math.floor((votes?.total || 0) * 0.7) },
                  { label: 'Claims mis à jour', value: Math.floor((votes?.total || 0) * 0.4) },
                ].map((s, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: '10px', background: C.bg, borderRadius: 8, border: `1px solid ${C.blueBorder}` }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.blue }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12, color: C.textSub, lineHeight: 1.6 }}>
                Vos participations, idées et témoignages alimentent directement l'IA PichAI. Chaque contribution améliore la précision des analyses collectives.
              </p>
            </div>
          </div>

          {/* Dernières actions avec narration */}
          <div className="card">
            <div className="card-header">
              <BoltIcon style={{ width: 16, color: C.black }} />
              <span className="card-title">Ce que vous avez récemment influencé</span>
              <span className="card-badge">{activity?.length || 0} {t('entries')}</span>
            </div>
            {activity?.length ? activity.slice(0,6).map((act,i) => {
              const cfg = TYPE_CFG[act.type] || TYPE_CFG.vote;
              const Icon = cfg.Icon;
              const narrative = act.type === 'vote' ? 'Vous avez voté sur' :
                                act.type === 'proof' ? 'Vous avez ajouté une preuve sur' :
                                act.type === 'comment' ? 'Vous avez commenté' :
                                act.type === 'question' ? 'Vous avez interrogé l\'IA sur' : 'Action sur';
              return (
                <div key={i} className="feed-row" onClick={() => act.claim_id && navigate(`/event/${act.claim_id}`)}>
                  <div className="feed-icon"><Icon style={{ width:16, color:C.black }} /></div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div className="feed-title">{narrative} — {act.claim_title || '—'}</div>
                    <div className="feed-meta">
                      <span className={`tag ${cfg.tag}`}>{t(cfg.labelKey)}</span>
                      <span style={{ fontSize:11, color:C.textMuted }}>{relativeTime(act.created_at)}</span>
                    </div>
                  </div>
                  <ChevronRightIcon style={{ width:14, color:C.textMuted }} />
                </div>
              );
            }) : (
              <div style={{ padding:'32px 18px', textAlign:'center' }}>
                <ClockIcon style={{ width:32, margin:'0 auto 10px', display:'block', color:C.textMuted, opacity:0.4 }} />
                <p style={{ color:C.textMuted, marginBottom:14 }}>Vous n'avez encore posé aucune action</p>
                <button className="btn-black" onClick={() => navigate('/claims')}>Commencer à explorer →</button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y">
          {/* Score citoyen */}
          <div className="card" style={{ padding:'20px 18px', textAlign:'center' }}>
            <p style={{ fontSize:12, fontWeight:600, color:C.textSub, marginBottom:14, textTransform:'uppercase' }}>{t('citizen_score')}</p>
            <ScoreRing pct={trustScore} size={88} color={C.black} />
            <p style={{ fontSize:12, color:C.textMuted, marginTop:14 }}>{t('score_description')}</p>
            <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:10 }}>
              {[{ label: t('votes'), value: votes.total||0, max:100 },
                { label: t('proofs_submitted'), value: evidence.total||0, max:20 },
                { label: t('accuracy'), value: contributions.accuracy_rate||0, max:100, unit:'%' },
              ].map((d,i) => (
                <div key={i} style={{ textAlign:'left' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:12, color:C.textSub }}>{d.label}</span>
                    <span style={{ fontSize:12, fontWeight:600 }}>{d.value}{d.unit||''}</span>
                  </div>
                  <div className="prog-track"><div className="prog-fill" style={{ width:`${Math.min((d.value/d.max)*100,100)}%`, background:C.black }} /></div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Top institutions actives */}
          <div className="card">
            <div className="card-header">
              <ChartBarIcon style={{ width:16, color:C.black }} />
              <span className="card-title">Top institutions actives</span>
              <span className="card-badge">Score PichAI</span>
            </div>
            {[
              { name:'Digicel Haiti', score:72, trend:'+4%', sector:'Télécom' },
              { name:'Sogebank',      score:65, trend:'-2%', sector:'Banque' },
              { name:'Sogener',       score:48, trend:'+1%', sector:'Énergie' },
            ].map((inst, i) => (
              <div key={i} className="claim-row" onClick={() => navigate(`/claims?claimant=${inst.name}`)}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: C.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, marginRight: 8, flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div className="claim-row-title">{inst.name}</div>
                <div className="claim-row-foot">
                  <span className="tag tag-blue">{inst.sector}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: inst.score >= 70 ? C.green : inst.score >= 50 ? C.amber : C.red }}>
                    {inst.score}%
                  </span>
                  <span style={{ fontSize: 11, color: inst.trend.startsWith('+') ? C.green : C.red, fontWeight: 600 }}>
                    {inst.trend}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Recommandations */}
          <div className="card">
            <div className="card-header">
              <FireIcon style={{ width:16, color:C.black }} />
              <span className="card-title">{t('recommended_for_you')}</span>
            </div>
            {recommendations?.length ? recommendations.slice(0,4).map(c => (
              <div key={c.id} className="claim-row" onClick={() => navigate(`/event/${c.id}`)}>
                <div className="claim-row-title">{c.title}</div>
                <div className="claim-row-foot">
                  <span className={`tag ${STATUS_CFG[c.status]?.tag || 'tag-neutral'}`}>{t(STATUS_CFG[c.status]?.labelKey || 'status_unverified')}</span>
                  {c.currentConsensus !== null && <span style={{ fontSize:12, fontWeight:700, color:c.currentConsensus>=50?C.green:C.red }}>{c.currentConsensus}%</span>}
                </div>
              </div>
            )) : (
              <div style={{ padding:'24px 18px', textAlign:'center' }}>
                <FireIcon style={{ width:32, margin:'0 auto 10px', display:'block', color:C.textMuted, opacity:0.4 }} />
                <p style={{ color:C.textMuted, marginBottom:14 }}>Aucune recommandation pour l'instant</p>
                <button className="btn-black" onClick={() => navigate('/claims')}>Explorer les faits →</button>
              </div>
            )}
          </div>

          {/* Écosystème surveillé */}
          <div className="card">
            <div className="card-header">
              <BuildingOffice2Icon style={{ width:16, color:C.black }} />
              <span className="card-title">Écosystème surveillé</span>
            </div>
            <div style={{ padding: '10px 18px', fontSize: 12, color: C.textMuted }}>
              Entreprises et sujets publics que vous analysez via vos interactions
            </div>
            {companiesWithMeta.length ? companiesWithMeta.map((c, i) => (
              <div key={i} className="claim-row" onClick={() => navigate(`/entreprise/${c.name}`)}>
                <div className="claim-row-title">{c.name}</div>
                <div className="claim-row-foot">
                  <span className="tag tag-blue">{c.sector}</span>
                  <span style={{ fontSize:12, color:C.textMuted }}>{c.reviews} faits</span>
                  {c.score > 0 && <span style={{ fontSize:12, fontWeight:700 }}>{c.score}%</span>}
                </div>
              </div>
            )) : (
              <div style={{ padding:'24px 18px', textAlign:'center' }}>
                <BuildingOffice2Icon style={{ width:32, margin:'0 auto 10px', display:'block', color:C.textMuted, opacity:0.4 }} />
                <p style={{ color:C.textMuted, marginBottom:14 }}>Aucune entreprise suivie</p>
                <button className="btn-black" onClick={() => navigate('/claims')}>Découvrir les entreprises →</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════ AUTRES ONGLETS ══════════════════════════════

// 2. Alertes & signaux
const AlertsTab = ({ followedEvents, insights, navigate, t }) => {
  const [filter, setFilter] = useState('all');
  const allSignals = [
    ...followedEvents.filter(e => e.attention_flag).map(e => ({
      type:'urgent', Icon: ExclamationTriangleIcon, title:e.title,
      detail: t('alert_claim_change', { consensus: e.currentConsensus ?? t('no_vote') }),
      tag: t('alert_needs_attention'), tagClass:'tag-red', route:`/event/${e.id}`,
    })),
    ...(insights || []).map(ins => ({
      type:'ia', Icon: CpuChipIcon, title:ins.title,
      detail: t('alert_ia_confidence', { confidence: ins.confidence, category: ins.category }),
      tag: t('alert_ia_analysis'), tagClass:'tag-blue',
    })),
    ...followedEvents.filter(e => !e.attention_flag).slice(0,5).map(e => ({
      type:'update', Icon: ChartBarIcon, title:e.title,
      detail: t('alert_update', { votes: e.participants?.toLocaleString()||0, status: t(STATUS_CFG[e.status]?.labelKey || 'status_unverified') }),
      tag: t('alert_update_label'), tagClass:'tag-neutral', route:`/event/${e.id}`,
    })),
  ];

  const shown = filter === 'all' ? allSignals : allSignals.filter(s => s.type === filter);
  const FILTERS = [
    { key:'all', label: t('all') },
    { key:'urgent', label: t('urgent'), Icon: ExclamationTriangleIcon },
    { key:'ia', label: t('ia'), Icon: CpuChipIcon },
    { key:'update', label: t('updates'), Icon: ChartBarIcon },
  ];

  return (
    <div className="fade-up">
      <div style={{ marginBottom:16, padding:'14px 18px', background:C.surface, borderRadius:10, border:`1px solid ${C.border}` }}>
        <p style={{ fontSize:13, color:C.textSub, lineHeight:1.6 }}>
          <strong style={{ color:C.text }}>{t('alerts_signals')}</strong> — {t('alerts_description')}
        </p>
      </div>
      <div className="tabs">
        {FILTERS.map(f => (
          <button key={f.key} className={`tab ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.Icon && <f.Icon style={{ width: 14, height: 14, marginRight: 4 }} />}{f.label}
          </button>
        ))}
      </div>
      <div className="card">
        <div className="card-header">
          <BellIcon style={{ width:16, color:C.black }} />
          <span className="card-title">{t('active_signals')}</span>
          <span className="card-badge">{shown.length} {t('result', { count: shown.length })}</span>
        </div>
        {shown.length ? shown.map((s,i) => {
          const IconComponent = s.Icon;
          return (
            <div key={i} className="feed-row" onClick={() => s.route && navigate(s.route)}>
              <div className="feed-icon">{IconComponent && <IconComponent style={{ width:16, color:C.black }} />}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="feed-title">{s.title}</div>
                <div className="feed-meta">
                  <span className={`tag ${s.tagClass}`}>{s.tag}</span>
                  <span style={{ fontSize:11, color:C.textMuted }}>{s.detail}</span>
                </div>
              </div>
              {s.route && <ChevronRightIcon style={{ width:14, color:C.textMuted }} />}
            </div>
          );
        }) : (
          <div style={{ padding:'40px 18px', textAlign:'center' }}>
            <BellIcon style={{ width:32, margin:'0 auto 10px', display:'block', color:C.textMuted, opacity:0.4 }} />
            <p style={{ color:C.textMuted }}>{t('no_signals')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// 3. Écosystème surveillé
const EcosystemTab = ({ followedEvents, navigate, t }) => {
  const companyMap = {};
  followedEvents.forEach(e => {
    const name = e.claimant || 'Autre';
    if (!companyMap[name]) companyMap[name] = { name, sector: 'Divers', claims: [], totalVotes: 0, consensusSum: 0 };
    companyMap[name].claims.push(e);
    companyMap[name].totalVotes += e.participants || 0;
    companyMap[name].consensusSum += e.currentConsensus || 0;
  });
  const companies = Object.values(companyMap).map(c => ({
    ...c,
    score: c.claims.length ? Math.round(c.consensusSum / c.claims.length) : 0,
    reviews: c.claims.length,
  }));

  return (
    <div className="fade-up space-y">
      <div className="card">
        <div className="card-header">
          <BuildingOffice2Icon style={{ width:16, color:C.black }} />
          <span className="card-title">Écosystème surveillé</span>
          <span className="card-badge">{companies.length} entités</span>
        </div>
        <div style={{ padding: '10px 18px', fontSize: 12, color: C.textMuted }}>
          Entreprises et sujets publics que vous analysez via vos interactions
        </div>
        {companies.length ? companies.map(c => (
          <React.Fragment key={c.name}>
            <div className="claim-row" onClick={() => navigate(`/entreprise/${c.name}`)}>
              <div className="claim-row-title">{c.name}</div>
              <div className="claim-row-foot">
                <span className="tag tag-blue">{c.sector}</span>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <ScoreRing pct={c.score} size={36} color={c.score>=70?C.green:c.score>=50?C.amber:C.red} />
                  <span style={{ fontSize:12, color:C.textMuted }}>{c.reviews} faits</span>
                </div>
              </div>
            </div>
            {c.claims.map((claim, j) => (
              <div key={j} className="claim-row" onClick={(e) => { e.stopPropagation(); navigate(`/event/${claim.id}`); }}
                style={{ paddingLeft: 32, background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                <div className="claim-row-title">{claim.title}</div>
                <div className="claim-row-foot">
                  <span className={`tag ${STATUS_CFG[claim.status]?.tag || 'tag-neutral'}`}>
                    {claim.status}
                  </span>
                  {claim.currentConsensus !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: claim.currentConsensus >= 50 ? C.green : C.red }}>
                        {claim.currentConsensus}%
                      </span>
                      <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>+{Math.floor(Math.random() * 15) + 1}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </React.Fragment>
        )) : (
          <div style={{ padding:'40px 18px', textAlign:'center', color:C.textMuted }}>Aucune entité suivie</div>
        )}
      </div>
    </div>
  );
};

// 4. Mes avis
const MyReviewsTab = ({ navigate, t }) => {
  const reviews = [
    { id:1, company:'Digicel Haïti', text:'Problème de réseau dans le Nord après la tempête', status:'confirmé', date:'2026-05-20' },
    { id:2, company:'Unibank', text:'Service client réactif pour un virement bloqué', status:'contesté', date:'2026-05-18' },
    { id:3, company:'Natcom', text:'Amélioration de la couverture 4G à Port-au-Prince', status:'confirmé', date:'2026-05-15' },
  ];
  return (
    <div className="fade-up">
      <div className="card">
        <div className="card-header">
          <ChatBubbleLeftRightIcon style={{ width:16, color:C.black }} />
          <span className="card-title">Mes avis entreprise</span>
          <span className="card-badge">{reviews.length} avis</span>
        </div>
        {reviews.map((rev) => (
          <div key={rev.id} className="feed-row" onClick={() => navigate(`/avis/${rev.id}`)}>
            <div style={{ flex:1 }}>
              <div className="feed-title">{rev.company} – {rev.text}</div>
              <div className="feed-meta">
                <span className={`tag ${rev.status === 'confirmé' ? 'tag-green' : 'tag-red'}`}>{rev.status}</span>
                <span style={{ fontSize:11, color:C.textMuted }}>{new Date(rev.date).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
            <ChevronRightIcon style={{ width:14, color:C.textMuted }} />
          </div>
        ))}
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}` }}>
          <button className="btn-black" onClick={() => navigate('/soumettre-avis')}><PencilSquareIcon style={{ width: 16, height: 16, marginRight: 6 }} />+ Nouvel avis</button>
        </div>
      </div>
    </div>
  );
};

// 5. Tendances du marché
const MarketTrendsTab = ({ insights, t }) => {
  const sectors = insights?.length ? insights.map(ins => ({
    name: ins.category,
    score: ins.confidence,
    trend: ins.impact_level === 'high' ? 'up' : 'flat',
    pct: ins.confidence,
    description: ins.description
  })) : [
    { name:'Télécom', score:78, trend:'up', pct:78, description:'Majorité des avis confirment les améliorations réseau.' },
    { name:'Banque', score:62, trend:'flat', pct:62, description:'Satisfaction en hausse pour les services digitaux.' },
    { name:'Énergie', score:45, trend:'down', pct:45, description:'Nombreux témoignages de coupures de courant.' },
    { name:'Transport', score:55, trend:'up', pct:55, description:'Expériences mitigées sur les transports en commun.' },
  ];

  const getSectorIcon = (name) => {
    switch(name) {
      case 'Télécom': return <BuildingOffice2Icon style={{ width: 16, height: 16 }} />;
      case 'Banque': return <BuildingOffice2Icon style={{ width: 16, height: 16 }} />;
      case 'Énergie': return <BoltIcon style={{ width: 16, height: 16 }} />;
      case 'Transport': return <ArrowTrendingUpIcon style={{ width: 16, height: 16 }} />;
      default: return <ChartBarIcon style={{ width: 16, height: 16 }} />;
    }
  };

  return (
    <div className="fade-up space-y">
      <div style={{ padding:'14px 18px', background:C.surface, borderRadius:10, border:`1px solid ${C.border}` }}>
        <p style={{ fontSize:13, color:C.textSub, lineHeight:1.6 }}>
          <strong>Perception citoyenne</strong> – Basée sur l'agrégation des avis, témoignages et réponses de l'IA pour chaque secteur.
        </p>
      </div>
      {sectors.map(s => (
        <div key={s.name} className="card">
          <div className="card-header">
            <span>{getSectorIcon(s.name)}</span>
            <span className="card-title">{s.name}</span>
            <span className={`metric-delta ${s.trend === 'up' ? 'delta-up' : s.trend === 'down' ? 'delta-down' : 'delta-flat'}`}>
              {s.trend === 'up' && <ArrowUpIcon style={{ width: 12, height: 12 }} />}
              {s.trend === 'up' ? ' en hausse' : s.trend === 'down' ? ' en baisse' : ' stable'}
            </span>
          </div>
          <div style={{ padding:'16px 18px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ flex:1 }}>
                <div className="prog-track" style={{ height:12 }}>
                  <div className="prog-fill" style={{ width:`${s.pct}%`, background:s.pct>=70?C.green:s.pct>=50?C.amber:C.red }} />
                </div>
              </div>
              <span style={{ fontWeight:700, fontSize:18 }}>{s.pct}%</span>
            </div>
            <p style={{ fontSize:12, color:C.textMuted, marginTop:8 }}>{s.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// 6. Mon impact
const ImpactTab = ({ personal, t }) => {
  const { user, votes={}, comments={}, evidence={}, contributions={} } = personal || {};
  const trustScore = user?.trust_score ? Math.round(user.trust_score * 100) : 0;
  return (
    <div className="fade-up space-y">
      <div className="impact-stats-grid">
        {[ 
          { label:t('votes'), value:votes.total||0, sub:t('this_month',{count:votes.this_month||0}) },
          { label:t('comments'), value:comments.total||0, sub:t('helpful',{count:comments.helpful||0}) },
          { label:t('proofs'), value:evidence.total||0, sub:t('approved',{count:evidence.approved||0}) },
          { label:t('accuracy'), value:`${contributions.accuracy_rate||0}%`, sub:t('predictions') },
          { label:t('trust_score'), value:`${trustScore}%`, sub:t('community_reputation') },
        ].map((s,i) => (
          <div key={i} className="metric-tile">
            <div className="metric-value" style={{ fontSize:22 }}>{s.value}</div>
            <div className="metric-label">{s.label}</div>
            <span className="metric-delta delta-flat">{s.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// 7. Activité
const ActivityTab = ({ activity, navigate, t }) => {
  const [filter, setFilter] = useState('all');
  const safe = Array.isArray(activity) ? activity : [];
  const shown = filter === 'all' ? safe : safe.filter(a => a.type === filter);

  return (
    <div className="fade-up">
      <div className="tabs">
        {[ { key:'all', label: t('all'), Icon: null },
           { key:'vote', label: t('votes'), Icon: ArrowUpIcon },
           { key:'proof', label: t('proofs'), Icon: DocumentTextIcon },
           { key:'comment', label: t('comments'), Icon: ChatBubbleLeftRightIcon },
           { key:'question', label: t('questions_ia'), Icon: QuestionMarkCircleIcon },
        ].map(f => (
          <button key={f.key} className={`tab ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.Icon && <f.Icon style={{ width: 14, height: 14, marginRight: 4 }} />}{f.label}
          </button>
        ))}
      </div>
      <div className="card">
        <div className="card-header">
          <ClockIcon style={{ width:16, color:C.black }} />
          <span className="card-title">{t('activity_history')}</span>
          <span className="card-badge">{shown.length} {t('actions')}</span>
        </div>
        {shown.length ? shown.map((act,i) => {
          const cfg = TYPE_CFG[act.type] || TYPE_CFG.vote;
          const IconComponent = cfg.Icon;
          return (
            <div key={i} className="feed-row" onClick={() => act.claim_id && navigate(`/event/${act.claim_id}`)}>
              <div className="feed-icon"><IconComponent style={{ width:16, color:C.black }} /></div>
              <div style={{ flex:1 }}>
                <div className="feed-title">{act.claim_title || '—'}</div>
                <div className="feed-meta">
                  <span className={`tag ${cfg.tag}`}>{t(cfg.labelKey)}</span>
                  <span style={{ fontSize:11, color:C.textMuted }}>{relativeTime(act.created_at)}</span>
                </div>
              </div>
              <ChevronRightIcon style={{ width:14, color:C.textMuted }} />
            </div>
          );
        }) : (
          <div style={{ padding:'40px 18px', textAlign:'center' }}><ClockIcon style={{ width:32, margin:'0 auto 10px', display:'block', color:C.textMuted, opacity:0.4 }} /><p>{t('no_actions')}</p></div>
        )}
      </div>
    </div>
  );
};

// 8. Analyses IA
const InsightsTab = ({ insights, t }) => (
  <div className="fade-up">
    <div style={{ marginBottom:16, padding:'14px 18px', background:C.blueBg, borderRadius:10, border:`1px solid ${C.blueBorder}` }}>
      <p style={{ fontSize:13, color:C.textSub, lineHeight:1.6 }}>
        <strong style={{ color:C.text }}>{t('ai_analysis')}</strong> — {t('ai_analysis_desc')}
      </p>
    </div>
    <div className="card">
      <div className="card-header">
        <CpuChipIcon style={{ width:16, color:C.black }} />
        <span className="card-title">{t('detected_signals')}</span>
        <span className="card-badge">{insights?.length||0} {t('analyses')}</span>
      </div>
      {insights?.length ? insights.map((ins,i) => (
        <div key={i} className="feed-row" style={{ cursor:'default', alignItems:'flex-start' }}>
          <div style={{ minWidth:64, textAlign:'center' }}>
            <div style={{ fontSize:22, fontWeight:700, fontFeatureSettings:'"tnum"', color:ins.confidence>=80?C.green:ins.confidence>=60?C.amber:C.red }}>{ins.confidence}%</div>
            <div style={{ fontSize:10, color:C.textMuted, marginBottom:4 }}>{t('confidence')}</div>
            <div className="prog-track"><div className="prog-fill" style={{ width:`${ins.confidence}%`, background:ins.confidence>=80?C.green:ins.confidence>=60?C.amber:C.red }} /></div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', gap:6, marginBottom:6, flexWrap:'wrap' }}>
              <span className="tag tag-blue">{ins.category}</span>
              {ins.impact_level==='high' && <span className="tag tag-red">{t('high_impact')}</span>}
            </div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>{ins.title}</div>
            <div style={{ fontSize:13, color:C.textSub, lineHeight:1.6 }}>{ins.description}</div>
            {ins.sources_count && <div style={{ fontSize:11, color:C.textMuted, marginTop:8 }}>{t('based_on_sources', { count: ins.sources_count })}</div>}
          </div>
        </div>
      )) : (
        <div style={{ padding:'40px 18px', textAlign:'center' }}><LightBulbIcon style={{ width:32, margin:'0 auto 10px', display:'block', color:C.textMuted, opacity:0.4 }} /><p style={{ color:C.textMuted }}>{t('no_analysis')}</p></div>
      )}
    </div>
  </div>
);

// 9. Paramètres
const SettingsTab = ({ user, t }) => (
  <div className="fade-up" style={{ maxWidth:600, margin:'0 auto', display:'flex', flexDirection:'column', gap:24 }}>
    <section className="card">
      <div className="card-header"><UserCircleIcon style={{ width:18, color:C.black }} /><span className="card-title">{t('profile_info')}</span></div>
      <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:14 }}>
        <div><label className="inp-label">{t('username')}</label><input type="text" defaultValue={user?.username||''} className="inp" /></div>
        <div><label className="inp-label">{t('email')}</label><input type="email" defaultValue={user?.email||''} className="inp" /></div>
        <button className="btn-black"><CheckCircleIcon style={{ width: 16, height: 16, marginRight: 6 }} />{t('save_changes')}</button>
      </div>
    </section>
    <section className="card">
      <div className="card-header"><BellIcon style={{ width:18, color:C.black }} /><span className="card-title">{t('notification_prefs')}</span></div>
      <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:16 }}>
        <label style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:14, color:C.textSub }}>
          {t('notif_claims_change')} <input type="checkbox" defaultChecked />
        </label>
        <label style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:14, color:C.textSub }}>
          {t('notif_weekly_summary')} <input type="checkbox" />
        </label>
      </div>
    </section>
  </div>
);

// ═══════════════ SKELETON ═══════════════════════════════════
const DashSkeleton = () => (
  <div className="dash">
    <style>{CSS}</style>
    <div className="mobile-tabs"><div style={{ padding:12 }}><Sk w={120} h={28} /></div></div>
    <div className="dash-layout">
      <aside className="sidebar">
        <div className="sidebar-section" style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {[...Array(9)].map((_,i)=><Sk key={i} h={34} />)}
        </div>
      </aside>
      <div className="main-content">
        <Sk h={60} /><Sk h={50} style={{ marginTop:16 }} /><div className="metrics-grid" style={{ marginTop:16 }}>{[...Array(4)].map((_,i)=><Sk key={i} h={100} />)}</div>
        <div className="two-col" style={{ marginTop:16 }}><div className="space-y"><Sk h={80} /><Sk h={160} /><Sk h={200} /></div><div className="space-y"><Sk h={200} /><Sk h={180} /></div></div>
      </div>
    </div>
  </div>
);

// ═══════════════ PAGE PRINCIPALE ═════════════════════════════
const DashboardPage = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    personal: null,
    followedEvents: [],
    recommendations: [],
    aiInsights: [],
    activity: [],
    happening: [],
  });

  const handleTabChange = (tab) => startTransition(() => setActiveTab(tab));

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [pR, iR, fR, rR, hR] = await Promise.allSettled([
          dashboardAPI.getPersonalDashboard(),
          dashboardAPI.getAIInsights(),
          eventsAPI.getFollowedEvents(),
          eventsAPI.getRecommendedEvents(),
          fetchHappening(),
        ]);
        if (cancelled) return;
        const ex = (r, fb) => r.status === 'fulfilled' ? (r.value?.data ?? fb) : fb;
        const personal = ex(pR, {})?.dashboard || ex(pR, {});
        const aiInsights = ex(iR, {})?.insights || [];
        const followedEvents = ensureArray(ex(fR, [])).map(normalizeClaim);
        const recommendations = ensureArray(ex(rR, [])).map(normalizeClaim);
        const happening = hR.status === 'fulfilled'
          ? (Array.isArray(hR.value) ? hR.value : hR.value?.events || [])
          : [];

        setData({
          personal,
          aiInsights,
          followedEvents,
          recommendations,
          activity: personal?.recent_activity || [],
          happening,
        });
      } catch {
        if (!cancelled) setError(t('load_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) return <DashSkeleton />;
  if (error) return (
    <div className="dash" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
      <style>{CSS}</style>
      <div style={{ textAlign:'center' }}>
        <ExclamationTriangleIcon style={{ width:40, color:C.red, marginBottom:14 }} />
        <p style={{ color:C.textSub, marginBottom:16 }}>{error}</p>
        <button className="btn-black" onClick={() => window.location.reload()}>{t('retry')}</button>
      </div>
    </div>
  );

  const personal = data.personal || {};
  const u = user || personal.user || {};

  const alertsCount = data.followedEvents.filter(e => e.attention_flag).length
                    + (data.aiInsights?.length || 0);

  return (
    <div className="dash">
      <style>{CSS}</style>
      <MobileTabs activeTab={activeTab} onTab={handleTabChange} t={t} alertsCount={alertsCount} />
      <div className="dash-layout">
        <Sidebar activeTab={activeTab} onTab={handleTabChange} navigate={navigate} t={t} alertsCount={alertsCount} />
        <main className="main-content">
          {activeTab === 'overview' && <OverviewTab personal={personal} insights={data.aiInsights} recommendations={data.recommendations} activity={data.activity} followedEvents={data.followedEvents} happening={data.happening} navigate={navigate} t={t} />}
          {activeTab === 'alerts' && <AlertsTab followedEvents={data.followedEvents} insights={data.aiInsights} navigate={navigate} t={t} />}
          {activeTab === 'ecosystem' && <EcosystemTab followedEvents={data.followedEvents} navigate={navigate} t={t} />}
          {activeTab === 'my_reviews' && <MyReviewsTab navigate={navigate} t={t} />}
          {activeTab === 'market_trends' && <MarketTrendsTab insights={data.aiInsights} t={t} />}
          {activeTab === 'activity' && <ActivityTab activity={data.activity} navigate={navigate} t={t} />}
          {activeTab === 'impact' && <ImpactTab personal={personal} t={t} />}
          {activeTab === 'insights' && <InsightsTab insights={data.aiInsights} t={t} />}
          {activeTab === 'settings' && <SettingsTab user={u} t={t} />}
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;